import * as React from 'react';
import { useEffect } from 'react';

import { DescriptionPage_SelfServeConfiguration_ModuleName as ModuleNames } from '__generated__/graphql-types';

import { useParams } from 'js/lib/useRouter';

import { useTracker } from '@coursera/event-pulse/react';

import {
  useEnterpriseLearningPathName,
  useEnterpriseLearningPathPageData,
} from 'bundles/enterprise-learning-path-description-page/contexts/EnterpriseLearningPathContext';
import { useSkillTagsData } from 'bundles/enterprise-learning-path-description-page/contexts/EnterpriseLearningPathSkillsContext';
import { useVspPageData } from 'bundles/enterprise-learning-path-description-page/hooks/useVspPageV2Data';
import Renderer from 'bundles/enterprise-learning-path-description-page/pages/EnterpriseSkillPathwayPageRenderer';
import EventingDataProvider from 'bundles/enterprise-learning-path-description-page/providers/EventingDataProvider';
import {
  LEARNING_PATH_NAV_ITEMS,
  getLearningPathPageNavigationLinks,
} from 'bundles/enterprise-learning-path-description-page/utils/getPageNavigationLinks';
import EnterpriseExperiments from 'bundles/epic/clients/Enterprise';
import PageInfoProvider from 'bundles/unified-description-page-common/providers/PageInfoProvider';
import PageNavigationProvider from 'bundles/unified-description-page-common/providers/PageNavigationProvider';
import StickyPanelProvider from 'bundles/unified-description-page-common/providers/StickyPanelProvider';

const EnterpriseSkillPathwayPageRootContent = () => {
  const pageData = useEnterpriseLearningPathPageData();
  const productTitle = useEnterpriseLearningPathName();
  const { data: vspPageData } = useVspPageData();
  const skillTagsData = useSkillTagsData();
  const { programSlug } = useParams();
  const track = useTracker();

  const isVspPageV2Enabled = EnterpriseExperiments.get('enableVSPXDPV2');

  useEffect(() => {
    if (pageData?.enterpriseBadge.id) {
      track('view_description_page', {
        product: {
          id: pageData.enterpriseBadge.id,
          verifiedSkillPath: true,
        },
      });
    }
    // this event only needs to be tracked once this component is rendered, hence disabled the rule below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!pageData || !productTitle || !skillTagsData || (isVspPageV2Enabled && !vspPageData)) {
    return null;
  }

  const pageConfig = {
    modules: [{ name: ModuleNames.Breadcrumbs }, { name: ModuleNames.ProductOverview }, { name: ModuleNames.Syllabus }],
  };

  const navigationLinks = getLearningPathPageNavigationLinks({ pageData, pageConfig });

  return (
    <PageInfoProvider programSlug={programSlug} navigationIdMap={LEARNING_PATH_NAV_ITEMS}>
      <EventingDataProvider productId={pageData.enterpriseBadge.id} productType="learning_path">
        <PageNavigationProvider navigationLinks={navigationLinks}>
          <StickyPanelProvider>
            <Renderer
              pageData={pageData}
              pageConfig={pageConfig}
              skillTagsData={skillTagsData}
              vspPageData={vspPageData}
            />
          </StickyPanelProvider>
        </PageNavigationProvider>
      </EventingDataProvider>
    </PageInfoProvider>
  );
};

export default EnterpriseSkillPathwayPageRootContent;
